'use client'

import React, { createContext, useCallback, useContext, useState } from 'react'
import { createPortal } from 'react-dom'

// Import icon from your own library
import CheckIcon from './icons/check'

type ToastProps = {
  title?: string
  description?: string
  variant?: 'default' | 'success' | 'error' | 'warning'
  duration?: number
  withIcon?: boolean
  withCloseButton?: boolean
  icon?: React.ReactNode
  position?:
    | 'left-top'
    | 'center-top'
    | 'right-top'
    | 'left-bottom'
    | 'center-bottom'
    | 'right-bottom'
}

type ToastContextType = {
  toast: (props: ToastProps) => void
}

const ToastContext = createContext<ToastContextType | null>(null)

export const ToastProvider = ({ children }: { children: React.ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [toastProps, setToastProps] = useState<ToastProps>({})

  const toast = useCallback((props: ToastProps) => {
    setToastProps(props)
    setIsOpen(true)
  }, [])

  const onClose = () => setIsOpen(false)

  const containerRef = React.useRef<HTMLDivElement>(null)
  const variantStyles = {
    default: 'bg-neutral-gray-700',
    success: 'bg-biome-green-50',
    error: 'bg-red-50',
    warning: 'bg-yellow-50'
  }

  const positionStyles = {
    'left-top': 'top-8 left-8',
    'center-top': 'top-8 left-1/2 transform -translate-x-1/2',
    'right-top': 'top-8 right-8',
    'left-bottom': 'bottom-8 left-8',
    'center-bottom': 'bottom-8 left-1/2 transform -translate-x-1/2',
    'right-bottom': 'bottom-8 right-8'
  }

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, setIsOpen])

  React.useEffect(() => {
    if (isOpen && toastProps.duration !== 0) {
      const timer = setTimeout(() => {
        setIsOpen(false)
      }, toastProps.duration || 3000)

      return () => clearTimeout(timer)
    }
  }, [isOpen, toastProps.duration])

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      {isOpen &&
        createPortal(
          <div
            style={{
              border: 'none',
              background: 'none',
              padding: 0,
              outline: 'none',
              zIndex: 99999
            }}
            ref={containerRef}
            className={`fixed w-[calc(100%-16px)] tablet-lg:w-full max-w-md transition-all duration-300 ease-in-out ${positionStyles[toastProps.position || 'center-bottom']}`}
          >
            <div
              className={`${variantStyles[toastProps.variant || 'default']} rounded-lg p-4 shadow-md transition-opacity`}
            >
              <div className="flex items-center justify-between gap-4">
                {toastProps.withIcon && (
                  <div className="flex flex-col items-start mt-0 mb-auto">
                    {toastProps.icon ? (
                      toastProps.icon
                    ) : (
                      <CheckIcon className="size-6 text-white" />
                    )}
                  </div>
                )}
                <div className="flex-1">
                  {toastProps.title && (
                    <p className="text-s-regular tablet-lg:body-sm-regular text-flat-white">
                      {toastProps.title}
                    </p>
                  )}
                  {toastProps.description && (
                    <p className="mt-1 text-s-regular tablet-lg:body-sm-regular text-flat-white">
                      {toastProps.description}
                    </p>
                  )}
                </div>
                {toastProps.withCloseButton && (
                  <button
                    onClick={onClose}
                    className="text-flat-white hover:text-neutral-gray-900 transition-colors heading-s tablet-lg:subheading-sm-regular"
                  >
                    Tutup
                  </button>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
    </ToastContext.Provider>
  )
}

export function useToast() {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context.toast
}
